import { PLAN_LIMITS } from './usageTracker';

/** Canonical upgrade path (matches PlanUpgradeUser / billing). */
export const PLAN_ORDER = ['starter', 'basic', 'pro', 'enterprise'];

const HEADROOM_OPS = 1.15;
const HEADROOM_STORAGE = 1.12;

/**
 * Map subscription fields to a canonical tier id.
 */
export function resolveCanonicalTierId(planId, planName, baseLimits) {
  const rawId = (planId || '').toString().toLowerCase().trim();
  if (PLAN_ORDER.includes(rawId)) return rawId;
  if (rawId === 'max') return 'enterprise';

  const name = (planName || '').toLowerCase();
  if (name.includes('enterprise')) return 'enterprise';
  if (/\bpro\b/.test(name) || name.includes('pro plan')) return 'pro';
  if (name.includes('basic')) return 'basic';
  if (name.includes('starter')) return 'starter';

  const opLimit = baseLimits?.firebase_operations;
  const stLimit = baseLimits?.storage_mb;
  if (typeof opLimit === 'number') {
    for (const id of PLAN_ORDER) {
      const L = PLAN_LIMITS[id];
      if (L.operations === opLimit && (typeof stLimit !== 'number' || L.storage === stLimit)) {
        return id;
      }
    }
    for (const id of PLAN_ORDER) {
      if (PLAN_LIMITS[id].operations >= opLimit) return id;
    }
    return 'enterprise';
  }

  return 'starter';
}

function tierIndex(tierId) {
  const i = PLAN_ORDER.indexOf(tierId);
  return i >= 0 ? i : 0;
}

/**
 * @param {Object} input
 * @param {Array} input.monthlyRows - from getCompanyUsageHistoryDetailed
 * @param {Object} input.analytics - peak, medianOps, avgLast3
 * @param {Object} input.totals - avgOps, months
 * @param {Object|null} input.period - planName, planId, baseLimits, usedOps, usedStorageMb
 */
export function computePlanSuggestion({ monthlyRows = [], analytics, totals, period }) {
  if (!period) {
    return {
      status: 'no_subscription',
      hasEnoughData: false,
      summary: 'Subscribe to get a recommendation based on your usage history.',
      reasons: [],
      fittingTierId: null,
      suggestedTierId: null,
      suggestedTierName: null,
      currentTierId: null,
      currentTierName: null,
      opsBaseline: 0,
      opsNeed: 0,
      storagePeak: 0,
      storageNeed: 0,
      headroomOpsPercent: 0,
      headroomStoragePercent: 0
    };
  }

  const peak = analytics?.peak?.operations ?? 0;
  const median = analytics?.medianOps ?? 0;
  const avgLast3 = analytics?.avgLast3 ?? 0;
  const avgAll = totals?.avgOps ?? 0;
  const usedThisPeriod = period?.usedOps ?? 0;

  const hasMonthly = monthlyRows.length > 0;
  const opsBaseline = hasMonthly
    ? Math.max(peak, median, avgLast3, avgAll)
    : Math.max(usedThisPeriod, avgAll);

  const opsNeed = Math.max(1, Math.ceil(opsBaseline * HEADROOM_OPS));

  let storagePeak = 0;
  for (const r of monthlyRows) {
    storagePeak = Math.max(storagePeak, typeof r.storageMb === 'number' ? r.storageMb : 0);
  }
  if (period?.usedStorageMb) {
    storagePeak = Math.max(storagePeak, period.usedStorageMb);
  }
  const storageNeed = Math.max(1, Math.ceil(storagePeak * HEADROOM_STORAGE));

  const findMinTierCovering = (ops, storageMb) => {
    for (let i = 0; i < PLAN_ORDER.length; i++) {
      const id = PLAN_ORDER[i];
      const L = PLAN_LIMITS[id];
      if (L.operations >= ops && L.storage >= storageMb) return i;
    }
    return PLAN_ORDER.length - 1;
  };

  const fittingIdx = findMinTierCovering(opsNeed, storageNeed);
  /** One tier above the minimum that fits usage — extra headroom for growth */
  let suggestedIdx = Math.min(fittingIdx + 1, PLAN_ORDER.length - 1);

  if (opsNeed > PLAN_LIMITS.enterprise.operations || storageNeed > PLAN_LIMITS.enterprise.storage) {
    suggestedIdx = PLAN_ORDER.length - 1;
  }

  const currentTierId = period
    ? resolveCanonicalTierId(period.planId, period.planName, period.baseLimits)
    : 'starter';
  const currentIdx = tierIndex(currentTierId);

  const suggestedId = PLAN_ORDER[suggestedIdx];
  const fittingId = PLAN_ORDER[fittingIdx];

  const suggested = PLAN_LIMITS[suggestedId];
  const fitting = PLAN_LIMITS[fittingId];

  const reasons = [];
  if (hasMonthly) {
    if (peak >= median * 1.2) reasons.push(`Peak month (${peak.toLocaleString('en-IN')} ops) drives the estimate.`);
    else reasons.push(`Typical load uses peak, median, and recent averages.`);
  } else {
    reasons.push('Limited history — estimate uses this billing period and overall average.');
  }
  reasons.push(`Minimum tier that fits projected load with buffer: ${fitting.name} (${fitting.operations.toLocaleString('en-IN')} ops / ${fitting.storage} MB).`);

  let status = 'recommend_upgrade';
  let summary = '';

  const hasEnoughData = hasMonthly || usedThisPeriod > 0;

  if (!hasEnoughData) {
    status = 'insufficient_data';
    summary =
      'Keep using the app for a few weeks — we will refine this suggestion as more monthly data appears.';
  } else if (suggestedIdx <= currentIdx) {
    status = 'already_capable';
    summary = `Your current plan (${PLAN_LIMITS[currentTierId]?.name || currentTierId}) already matches or exceeds this usage profile.`;
  } else {
    summary = `Your usage fits "${fitting.name}" with buffer; "${suggested.name}" is one step up for extra headroom as you grow.`;
  }

  if (period?.hasTopups) {
    reasons.push('You have active top-ups; entitlements may be higher than the base plan alone.');
  }

  return {
    hasEnoughData,
    opsBaseline: Math.round(opsBaseline),
    opsNeed,
    storagePeak: Math.round(storagePeak * 10) / 10,
    storageNeed: Math.round(storageNeed * 10) / 10,
    fittingTierId: fittingId,
    fittingTierName: fitting.name,
    suggestedTierId: suggestedId,
    suggestedTierName: suggested.name,
    suggestedPrice: suggested.price,
    suggestedOpsLimit: suggested.operations,
    suggestedStorageMb: suggested.storage,
    currentTierId,
    currentTierName: PLAN_LIMITS[currentTierId]?.name || currentTierId,
    currentIdx,
    suggestedIdx,
    status,
    summary,
    reasons,
    headroomOpsPercent: Math.round((HEADROOM_OPS - 1) * 100),
    headroomStoragePercent: Math.round((HEADROOM_STORAGE - 1) * 100)
  };
}
